import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DASHBOARD } from '../../router/routes';
import { RootState } from '../../store/store';
import {
  DEFAULT_REDUCING_MEDIA_QUERY,
  toggleSidebarMenuOpen,
  toggleSidebarMenuReduced,
} from '../../store/global-slice';
import Hamburger from '../common/hamburger';
import logo from '../../assets/logo.png';
import logo2x from '../../assets/logo@2x.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDoubleLeft,
  faChevronDoubleRight,
} from '@fortawesome/pro-regular-svg-icons';
import { buildMenu, menu, sidebarGroupItem } from './menuData';
import css from './index.module.css';
import { route } from '../../services/utils';

export default function Sidebar() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    isSidebarMenuReduced,
    isSidebarMenuOpen,
    farmId,
    season,
    farmUser,
    farm,
  } = useSelector((state: RootState) => ({
    isSidebarMenuReduced: state.global.isSidebarMenuReduced,
    isSidebarMenuOpen: state.global.isSidebarMenuOpen,
    farmId: state.global?.farm?.id,
    season: state.global?.season,
    farmUser: state.global.farmUser,
    farm: state.global.farm,
  }));

  const [openedKey, setOpenedKey] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const location = useLocation();

  const { selectedFarm } = useSelector((state: RootState) => ({
    selectedFarm: state.global.farm,
  }));

  useSetActiveKeyOnLocationEnter(location, setActiveKey);
  useCloseMobileMenuOnLocationEnter(location, dispatch);
  useReduceMobileMenuOnLocationEnter(location, dispatch);

  const toggleReduced = () => dispatch(toggleSidebarMenuReduced());
  const openGroup = (key: string) => {
    setOpenedKey(key === openedKey ? null : key);
  };
  const wrapperClasses = () =>
    `${css['wrapper']} `
      .concat(`${isSidebarMenuOpen ? css['mobile-opened'] : ''} `)
      .concat(`${isSidebarMenuReduced ? css['reduced'] : ''} `);
  const menuItemClasses = (group: sidebarGroupItem) => {
    return `${group.key === activeKey && css['active']} ${
      group.key === openedKey && css['opened']
    } ${group.key}-group`;
  };

  const [builtMenu, setBuiltMenu] = useState([]);
  useEffect(() => {
    setBuiltMenu(buildMenu(farmUser, farm.sectors as string[]));
  }, [farmUser, setBuiltMenu, farm.sectors]);

  return (
    <div className={wrapperClasses()}>
      <div className={css['sidebar']}>
        <div className={css['hamburger-wrapper']}>
          <Hamburger theme="light" />
        </div>

        <div className={css['logo']}>
          <Link to={route(DASHBOARD, { farmId: selectedFarm.id })}>
            <img
              src={logo}
              srcSet={logo + ' 1x,' + logo2x + ' 2x'}
              alt="Mans Lauks"
            />
          </Link>
        </div>

        <div className={css['menu']}>
          <ul>
            {builtMenu.map((group, index) => (
              <li
                key={index}
                className={menuItemClasses(group)}
                style={{
                  ['--menu-accent-color' as any]: group.colorHover,
                  ['--menu-color' as any]: group.color,
                }}
                onClick={() => openGroup(group.key)}
              >
                <div className={css['title']}>
                  <div className={css['title-name']}>
                    {t(group.heading.title)}
                  </div>
                  <FontAwesomeIcon icon={group.heading.icon} />
                  <div
                    className={css['decorator']}
                    style={{ backgroundColor: group.color }}
                  />
                </div>

                <ul className={css['items']}>
                  {group.items.map((item: any, index: number) => (
                    <li key={index}>
                      <NavLink
                        to={route(item.link, {
                          ...item.params,
                          farmId,
                          season,
                        })}
                        // className={item.isActive ? css['active'] : ''}
                        activeClassName={css['active']}
                      >
                        <FontAwesomeIcon icon={item.icon} />
                        <span>{t(item.label)}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className={css['toggle']} onClick={toggleReduced}>
        <span className={css['icon-holder']}>
          <FontAwesomeIcon
            icon={
              isSidebarMenuReduced ? faChevronDoubleRight : faChevronDoubleLeft
            }
          />
        </span>

        <span className={css['toggle-title']}>
          {t('components.sidebar.reduce-sidebar')}
        </span>
      </div>
    </div>
  );
}

const useSetActiveKeyOnLocationEnter = (
  location: any,
  setActiveKey: (value: unknown) => void
) => {
  useEffect(() => {
    let key = null;
    for (let menuItem of menu) {
      for (let subItem of menuItem.items) {
        subItem.isActive = false;
        if (matchPath(location.pathname, subItem.link)) {
          key = menuItem.key;
          subItem.isActive = true;
        }
      }
    }
    setActiveKey(key);
  }, [location.pathname, setActiveKey]);
};

const useCloseMobileMenuOnLocationEnter = (location: any, dispatch: any) => {
  useEffect(() => {
    dispatch(toggleSidebarMenuOpen(false));
  }, [location.pathname, dispatch]);
};

const useReduceMobileMenuOnLocationEnter = (location: any, dispatch: any) => {
  useEffect(() => {
    if (window.matchMedia(DEFAULT_REDUCING_MEDIA_QUERY).matches)
      dispatch(toggleSidebarMenuReduced(true));
  }, [location.pathname, dispatch]);
};
